import React from 'react';
import { Dropdown, Button, MenuProps } from 'antd';
import { GlobalOutlined, DownOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import MAFlag from '../../assets/images/flag/morocco.png';
import FRFlag from '../../assets/images/flag/france.png';
import USFlag from '../../assets/images/flag/usa.png';

interface LanguageSwitcherProps {
    variant?: 'light' | 'dark';
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ variant = 'dark' }) => {
    const { i18n } = useTranslation();

    const languageLookup: Record<string, { flag: string; label: string }> = {
        fr: { flag: FRFlag, label: 'Français' },
        en: { flag: USFlag, label: 'English' },
        ar: { flag: MAFlag, label: 'العربية' },
    };

    const activeLanguage = languageLookup[i18n.language] || languageLookup.fr;
    const isMobile = window.innerWidth <= 768;

    const palette = variant === 'dark' 
        ? {
            background: 'rgba(7, 25, 40, 0.82)',
            border: 'rgba(255,255,255,0.32)',
            text: 'rgba(255,255,255,0.92)',
            hover: 'rgba(255,255,255,0.12)',
        }
        : {
            background: 'linear-gradient(135deg, #ffffff, #f6fafc)',
            border: '#e0e7f1',
            text: '#0f172a',
            hover: 'rgba(15,23,42,0.08)',
        };

    const languageMenu: MenuProps['items'] = [
        {
            key: 'fr',
            label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <img src={FRFlag} alt="Français" style={{ width: 18 }} />
                    Français
                </div>
            ),
        },
        {
            key: 'en',
            label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <img src={USFlag} alt="English" style={{ width: 18 }} />
                    English
                </div>
            ),
        },
        {
            key: 'ar',
            label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <img src={MAFlag} alt="العربية" style={{ width: 18 }} />
                    العربية
                </div>
            ),
        },
    ];

    const handleLanguageMenuClick: MenuProps['onClick'] = ({ key }) => {
        if (typeof key === 'string') {
            // Only use i18n.changeLanguage - it handles localStorage automatically
            // No need for setLang since user is not authenticated on auth pages
            i18n.changeLanguage(key);
        }
    };

    const triggerStyle: React.CSSProperties = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 16px',
        borderRadius: 16,
        border: `1px solid ${palette.border}`,
        background: palette.background,
        color: palette.text,
        fontSize: 14,
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: variant === 'dark' 
            ? '0 4px 12px rgba(0,0,0,0.24)' 
            : '0 8px 18px rgba(15,23,42,0.08)',
    };

    const iconButtonStyle: React.CSSProperties = {
        width: 40,
        height: 40,
        borderRadius: 16,
        border: `1px solid ${palette.border}`,
        background: palette.background,
        color: palette.text,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: variant === 'dark' 
            ? '0 4px 12px rgba(0,0,0,0.24)' 
            : '0 8px 18px rgba(15,23,42,0.08)',
    };

    return (
        <Dropdown
            menu={{ items: languageMenu, onClick: handleLanguageMenuClick }}
            trigger={['click']}
            placement="bottomRight"
        >
            {isMobile ? (
                <Button 
                    type="text" 
                    style={iconButtonStyle}
                    icon={<GlobalOutlined />}
                />
            ) : (
                <button type="button" style={triggerStyle}>
                    <img 
                        src={activeLanguage.flag} 
                        alt={activeLanguage.label} 
                        style={{ width: 18, height: 18, objectFit: 'contain' }} 
                    />
                    <span>{activeLanguage.label}</span>
                    <DownOutlined style={{ fontSize: 10, opacity: 0.7 }} />
                </button>
            )}
        </Dropdown>
    );
};

export default LanguageSwitcher;
